import React, { useRef } from 'react';
import { createContext, useEffect, useMemo, useState, useCallback } from 'react';

import { useRelayEnvironment } from 'react-relay';

import { useFlags } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';

import { setMicrositeUser } from '../../common/analytics/analytics-client';
import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import { setSentryUser } from '../../common/sentry';
import { getConfig } from '../../config';

import { attemptClientLogin, cleanupClientAuthFlow, consumeLoginPendingMarker } from './auth-flow';
import { type LoggedInUser, type UserContextValue } from './types';
import { queryUserProfile } from './utils';

export const UserContext: React.Context<UserContextValue> =
	createContext<UserContextValue>(undefined);

type UserProviderProps = {
	children: React.ReactNode;
	onUserLoginComplete?: (user: LoggedInUser) => Promise<void>;
	isDTAC?: boolean;
};

/**
 * UserProvider manages the authentication state and user profile data.
 *
 * Authentication Flow:
 * 1. On mount, attempts to fetch user profile data
 * 2. If profile data is incomplete or fetch fails, triggers handleAuthFailure
 * 3. handleAuthFailure will:
 *    - If automatic auth fails, show a flag to user to manually clear cookies
 *
 * The heartbeat component also triggers handleAuthFailure if session becomes invalid.
 */
export function UserProvider({
	children,
	onUserLoginComplete,
	isDTAC = false,
}: UserProviderProps): React.JSX.Element {
	const [identity, setIdentity] = useState<LoggedInUser>({
		id: '',
		picture: '',
		accountId: '',
		name: '',
		nickname: '',
		email: '',
		staffId: '',
		isAtlassianEmployee: false,
		organisation: '',
		jobTitle: '',
		orgId: '',
	});
	const loginRedirectFailed = useRef<boolean>(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [isBlocked, setIsBlocked] = useState(false);
	const environment = useRelayEnvironment();
	const { showFlag } = useFlags();
	const { fireEvent } = useAnalyticsEvents();

	const handleAuthFailure = useCallback(async () => {
		// if (hasAutomaticAuthFailed()) {
		// 	loginRedirectFailed.current = true;
		// 	return;
		// }
		if (
			isDTAC ||
			getConfig().isLocalDev ||
			localStorage.getItem('skip-auth-validation') === 'true'
		) {
			return;
		}
		// TODO: Remove this in the future. This forces the user to be logged in as we want
		// the feature gate to be evaluated so only a subset of users are allowed to access the microsite.
		attemptClientLogin();
	}, [isDTAC]);

	const fetchUserProfile = useCallback(async () => {
		const res = await queryUserProfile(environment);

		if (res.success) {
			const { identity } = res;

			if (identity.accountId) {
				// Execute post login if provided
				if (onUserLoginComplete) {
					await onUserLoginComplete?.(identity);
				}

				setIdentity(identity);
				setMicrositeUser(identity.accountId, identity.orgId, null);
				setSentryUser(identity.accountId);

				if (consumeLoginPendingMarker()) {
					fireEvent('track.user.loggedIn', {});
				}

				// Evaluate access *before* flipping `isAuthed` (i.e. before
				// `setIdentity`) so that downstream consumers — including the
				// lazy-loaded `LoggedInGate` chunk — never see a transient
				// "authed and not blocked" state for users who shouldn't be
				// allowed in.
				const isAllowedToAccessMicrositeFG = fg('twg_microsite_access_control');
				const isAtlassianStaff =
					identity.isAtlassianEmployee || identity.email.includes('@atlassian.com');

				if (!isAllowedToAccessMicrositeFG && !isAtlassianStaff && !isDTAC) {
					setIsBlocked(true);
					// Kick off the navigation. The browser tab will be replaced,
					// but until that happens the `isBlocked` short-circuit below
					// guarantees we render nothing from the microsite.
					window.open('https://www.atlassian.com', '_self');
					return;
				}
			} else {
				setIdentity(identity);
			}
		} else {
			setError(true);
			await handleAuthFailure();
		}
	}, [environment, handleAuthFailure, onUserLoginComplete, fireEvent, isDTAC]);

	useEffect(() => {
		fetchUserProfile().finally(() => setLoading(false));
	}, [fetchUserProfile]);

	useEffect(() => {
		if (!loginRedirectFailed.current) {
			return;
		}

		// For whatever reason, attempting to redirect to login has failed and we need
		// to provide a way for the user to manually resolve their issue.
		showFlag({
			id: 'redirection-failed',
			title: 'Automatic login has failed',
			description: 'Please clear your cookies and try again, or use incognito mode.',
		});

		cleanupClientAuthFlow();
	}, [loginRedirectFailed, showFlag]);

	// `isAuthed` is the single boolean source of truth for "the user is
	// signed in and we know who they are". Pre-computed here so consumers
	// (analytics hooks, route gating in `home/index.tsx`, etc.) don't have
	// to re-derive it on every render.
	const isAuthed = !loading && !error && !isBlocked && identity.accountId.length > 0;

	const value = useMemo(
		() => ({
			identity,
			loading,
			error,
			isAuthed,
			isBlocked,
			attemptUserLogin: attemptClientLogin,
			handleAuthFailure,
		}),
		[identity, loading, error, isAuthed, isBlocked, handleAuthFailure],
	);

	// Hard short-circuit: if the user is not allowed to view the microsite,
	// render nothing while the browser navigation kicks in. This prevents any
	// gated UI (including lazy-loaded chunks) from being rendered in the brief
	// window between the FG check and the actual page navigation.
	if (isBlocked) {
		return <UserContext.Provider value={value}>{null}</UserContext.Provider>;
	}

	return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}
